import { useApi } from '@hooks/useApi';
import { BenchUser, ApiError } from '@root/globalTypes';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { queryKeys } from '@utils/consts';
import { CreateBenchUserData } from '@services/types';

export type BenchUserResponse = BenchUser | ApiError;

export function isFailure(data: BenchUserResponse): data is ApiError {
  return (data as ApiError)?.message !== undefined;
}

type CreateBenchUserParams = {
  callback: (data: BenchUser) => void;
};

export function useCreate({ callback }: CreateBenchUserParams) {
  const { query } = useApi();
  const queryClient = useQueryClient();
  const [apiError, setApiError] = useState<string>('');

  const { mutate, isPending } = useMutation({
    mutationFn: (data: CreateBenchUserData): Promise<BenchUser> => {
      return query({
        path: 'v1/staff/bench_users',
        data,
        opt: {
          method: 'POST',
        },
      });
    },
    onSettled: (data) => {
      if (!data) return;
      if (isFailure(data)) {
        setApiError(data.message);
      } else {
        callback(data);
      }
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: queryKeys.benchUsers });
    },
  });

  return {
    mutate,
    isPending,
    error: apiError,
  };
}
